import {
  Cache,
  DataFields,
  OptimisticMutationConfig,
  UpdatesConfig,
  Variables,
} from "@urql/exchange-graphcache";
import {
  PlaylistFieldsFragment,
  PlaylistFieldsFragmentDoc,
  PlaylistItemInput,
} from "../../gql/graphql";

type PlaylistItemFields = PlaylistFieldsFragment["items"][number];

const playlistTypename = "PlaylistUB";

function readPlaylist(cache: Cache, id: string): PlaylistFieldsFragment | null {
  return cache.readFragment(PlaylistFieldsFragmentDoc, {
    __typename: playlistTypename,
    id,
  });
}

/** Optimistic result for a change of the items: the whole list, otherwise the optimistic list replaces it. */
function withItems(
  cache: Cache,
  id: string,
  change: (items: PlaylistItemFields[]) => PlaylistItemFields[],
) {
  const playlist = readPlaylist(cache, id);
  return {
    __typename: playlistTypename,
    id,
    ...(playlist ? { items: change(playlist.items) } : {}),
  };
}

function toItemFields(item: PlaylistItemInput): PlaylistItemFields {
  return {
    __typename: "PlaylistItemUB",
    id: item.id,
    position: item.position,
    songId: item.songId ?? null,
    title: item.title ?? null,
    note: item.note ?? null,
  };
}

function changeItem(
  itemId: string,
  change: Partial<PlaylistItemFields>,
): (items: PlaylistItemFields[]) => PlaylistItemFields[] {
  return (items) =>
    items.map((i) => (i.id === itemId ? { ...i, ...change } : i));
}

const asString = (value: unknown) => value as string;

export const playlistOptimistic: OptimisticMutationConfig = {
  createPlaylist: (args) => ({
    __typename: playlistTypename,
    id: asString(args.id),
    title: asString(args.title),
    belongsToCrew: (args.crewId as string | null | undefined) ?? null,
    tags: args.tags as string[],
    createdAt: asString(args.changedAt),
    updatedAt: asString(args.changedAt),
    items: (args.items as PlaylistItemInput[]).map(toItemFields),
  }),
  renamePlaylist: (args) => ({
    __typename: playlistTypename,
    id: asString(args.id),
    title: asString(args.title),
  }),
  setPlaylistTags: (args) => ({
    __typename: playlistTypename,
    id: asString(args.id),
    tags: args.tags as string[],
  }),
  assignPlaylistToCrew: (args) => ({
    __typename: playlistTypename,
    id: asString(args.id),
    belongsToCrew: (args.crewId as string | null | undefined) ?? null,
  }),
  deletePlaylist: () => null,
  addPlaylistItem: (args, cache) =>
    withItems(cache, asString(args.id), (items) => {
      const item = args.item as PlaylistItemInput;
      return items.some((i) => i.id === item.id)
        ? items
        : [...items, toItemFields(item)];
    }),
  movePlaylistItem: (args, cache) =>
    withItems(
      cache,
      asString(args.id),
      changeItem(asString(args.itemId), { position: asString(args.position) }),
    ),
  setPlaylistItemTitle: (args, cache) =>
    withItems(
      cache,
      asString(args.id),
      changeItem(asString(args.itemId), {
        title: (args.title as string | null | undefined) ?? null,
      }),
    ),
  setPlaylistItemNote: (args, cache) =>
    withItems(
      cache,
      asString(args.id),
      changeItem(asString(args.itemId), {
        note: (args.note as string | null | undefined) ?? null,
      }),
    ),
  removePlaylistItem: (args, cache) =>
    withItems(cache, asString(args.id), (items) =>
      items.filter((i) => i.id !== asString(args.itemId)),
    ),
};

/**
 * Keeps the list of all playlists (`Query.playlists`) in line with a mutation result: `null` means the playlist is
 * deleted or no longer accessible (ADR-11), anything else means it exists. Idempotent, and it also runs for optimistic
 * results.
 */
function updatePlaylistList(cache: Cache, id: string, exists: boolean) {
  const list = cache.resolve("Query", "playlists") as string[] | null;
  if (!list) {
    // Not loaded yet, the next sync brings the list
    return;
  }
  const key = cache.keyOfEntity({ __typename: playlistTypename, id });
  if (!key) {
    return;
  }
  if (exists && !list.includes(key)) {
    cache.link("Query", "playlists", [...list, key]);
  } else if (!exists && list.includes(key)) {
    cache.link(
      "Query",
      "playlists",
      list.filter((k) => k !== key),
    );
  }
}

const playlistMutationNames = Object.keys(playlistOptimistic);

export const playlistMutationUpdates: UpdatesConfig["Mutation"] =
  Object.fromEntries(
    playlistMutationNames.map((name) => [
      name,
      (result: DataFields, args: Variables, cache: Cache) =>
        updatePlaylistList(cache, asString(args.id), result[name] != null),
    ]),
  );
